'use client'

import * as React from 'react'
import { useEffect } from 'react'

/**
 * Route-level error boundary for everything under the root layout. Next
 * renders this (in place of the crashed segment) when a client render
 * throws, instead of unmounting the whole tree to a blank screen. Gives
 * the user a recoverable surface — `reset()` re-renders the segment, and
 * a hard reload pulls a fresh bundle (the usual cause: a deployed page
 * lagging an API field change). Must be a Client Component per Next.
 */
export default function RouteError({
  error,
  reset,
}: {
  readonly error: Error & { digest?: string }
  readonly reset: () => void
}): React.ReactElement {
  useEffect(() => {
    // Surface it for diagnosis; the digest correlates with server logs.
    console.error('Route error boundary caught:', error)
  }, [error])

  return (
    <main style={wrapStyle}>
      <div style={cardStyle}>
        <h1 style={{ margin: '0 0 8px', fontSize: 18 }}>Something went wrong</h1>
        <p style={{ color: '#999', fontSize: 14, margin: '0 0 16px' }}>
          This page hit an unexpected error. Try again — if it persists, reload to pull the latest
          version.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => reset()} style={primaryBtnStyle}>
            Try again
          </button>
          <button
            type="button"
            onClick={() => {
              window.location.reload()
            }}
            style={secondaryBtnStyle}
          >
            Reload page
          </button>
        </div>
        {error.digest ? (
          <p style={{ color: '#555', fontSize: 11, margin: '14px 0 0' }}>Ref: {error.digest}</p>
        ) : null}
      </div>
    </main>
  )
}

const wrapStyle: React.CSSProperties = {
  minHeight: '60vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 32,
}
const cardStyle: React.CSSProperties = {
  maxWidth: 440,
  background: '#141414',
  border: '1px solid #2a2a2a',
  borderRadius: 10,
  padding: 24,
}
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 6,
  border: 'none',
  background: '#4ea1ff',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
}
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 6,
  border: '1px solid #2a2a2a',
  background: '#161616',
  color: '#bbb',
  fontSize: 14,
  cursor: 'pointer',
}
